import { env } from '../lib/env';
import { supabase } from '../lib/supabase';
import { logger } from './loggingService';

interface OAuthConfig {
  authUrl: string;
  clientId: string;
  redirectUri: string;
  scope: string;
}

function getOAuthConfig(provider: 'github' | 'jira'): OAuthConfig | null {
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    if (provider === 'github') {
      return {
        authUrl: 'https://github.com/login/oauth/authorize',
        clientId: env.VITE_GITHUB_CLIENT_ID || '',
        redirectUri: `${origin}/auth/github/callback`,
        scope: 'repo read:user user:email',
      };
    }
    if (provider === 'jira') {
      return {
        authUrl: 'https://auth.atlassian.com/authorize',
        clientId: env.VITE_JIRA_CLIENT_ID || '',
        redirectUri: `${origin}/auth/jira/callback`,
        scope: 'read:jira-work write:jira-work manage:jira-project read:jira-user read:me read:account offline_access',
      };
    }
    return null;
  } catch {
    return null;
  }
}

export class OAuthService {
  private static popupWindow: Window | null = null;
  private static popupCheckInterval: number | null = null;

  static initiateOAuthPopup(provider: string, userId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const config = getOAuthConfig(provider as 'github' | 'jira');
      if (!config) {
        reject(
          new Error(`OAuth configuration not found for provider: ${provider}`)
        );
        return;
      }

      if (!config.clientId) {
        reject(
          new Error(
            `OAuth client ID not configured for ${provider}. Please contact support.`
          )
        );
        return;
      }

      const state = crypto.randomUUID();
      const stateData = {
        state,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(
        `oauth_state_${provider}`,
        JSON.stringify(stateData)
      );
      sessionStorage.setItem(`oauth_user_id_${provider}`, userId);

      const params = new URLSearchParams({
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        scope: config.scope,
        response_type: 'code',
        state,
      });

      if (provider === 'jira') {
        params.append('audience', 'api.atlassian.com');
        params.append('prompt', 'consent');
      }

      const authUrl = `${config.authUrl}?${params.toString()}`;

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      this.popupWindow = window.open(
        authUrl,
        `${provider}_oauth`,
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
      );

      if (!this.popupWindow) {
        reject(
          new Error('Popup was blocked. Please allow popups for this site.')
        );
        return;
      }

      if (this.popupCheckInterval) {
        clearInterval(this.popupCheckInterval);
      }

      this.popupCheckInterval = window.setInterval(() => {
        if (!this.popupWindow || this.popupWindow.closed) {
          if (this.popupCheckInterval) {
            clearInterval(this.popupCheckInterval);
            this.popupCheckInterval = null;
          }

          const success = sessionStorage.getItem(`oauth_success_${provider}`);
          sessionStorage.removeItem(`oauth_success_${provider}`);
          sessionStorage.removeItem(`oauth_state_${provider}`);
          sessionStorage.removeItem(`oauth_user_id_${provider}`);

          if (success === 'true') {
            resolve();
          } else {
            reject(new Error('OAuth flow was cancelled or failed'));
          }
        }
      }, 500);
    });
  }

  static handleOAuthCallback(
    provider: string,
    _code: string,
    state: string
  ): void {
    const savedStateData = sessionStorage.getItem(`oauth_state_${provider}`);

    if (!savedStateData) {
      logger.error('OAuth state not found', { provider });
      sessionStorage.setItem(`oauth_success_${provider}`, 'false');
      window.close();
      return;
    }

    let savedState: string;
    try {
      const parsedState = JSON.parse(savedStateData);
      savedState = parsedState.state;
    } catch {
      // Fallback for old format
      savedState = savedStateData;
    }

    if (state !== savedState) {
      logger.error('OAuth state mismatch', {
        provider,
        received: state,
        expected: savedState,
      });
      sessionStorage.setItem(`oauth_success_${provider}`, 'false');
      window.close();
      return;
    }

    sessionStorage.setItem(`oauth_success_${provider}`, 'true');
    window.close();
  }

  static async exchangeCodeForToken(
    provider: string,
    code: string,
    userId: string
  ): Promise<void> {
    const { data, error } = await supabase.functions.invoke(
      `${provider}-oauth-callback`,
      {
        body: { code, userId },
      }
    );

    if (error) {
      throw new Error(`Failed to exchange OAuth code: ${error.message}`);
    }

    return data;
  }

  static initiateOAuthFlow(provider: string): void {
    const config = getOAuthConfig(provider as 'github' | 'jira');
    if (!config) {
      throw new Error(
        `OAuth configuration not found for provider: ${provider}`
      );
    }

    if (!config.clientId) {
      logger.warn('OAuth client ID not configured', { provider });
      return;
    }

    const params = new URLSearchParams({
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      scope: config.scope,
      response_type: 'code',
      state: crypto.randomUUID(),
    });

    if (provider === 'jira') {
      params.append('audience', 'api.atlassian.com');
      params.append('prompt', 'consent');
    }

    const authUrl = `${config.authUrl}?${params.toString()}`;
    window.location.href = authUrl;
  }

  static getRedirectUri(provider: string): string {
    const config = getOAuthConfig(provider as 'github' | 'jira');
    return config?.redirectUri || '';
  }

  static initiateGitHubLogin = (userId: string) =>
    OAuthService.initiateOAuthPopup('github', userId);
  static initiateJiraLogin = (userId: string) =>
    OAuthService.initiateOAuthPopup('jira', userId);

  static async getGitHubRepositories(userId: string): Promise<any[]> {
    const { data: connection } = await supabase
      .from('user_connections')
      .select('access_token')
      .eq('user_id', userId)
      .eq('provider', 'github')
      .eq('status', 'active')
      .maybeSingle();

    if (!connection?.access_token) {
      throw new Error('GitHub not connected');
    }

    const response = await fetch(
      'https://api.github.com/user/repos?per_page=100&sort=updated',
      {
        headers: {
          Authorization: `Bearer ${connection.access_token}`,
          Accept: 'application/vnd.github+json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch repositories');
    }

    return response.json();
  }
}
